import { useState, useMemo } from 'react'
import { cn } from '../../utils'
import type { CreditBlock } from '@highlight/common/types'
import { Toggle } from './Toggle'
import { FadingScrollable } from './FadingScrollable'
import { twMerge } from 'tailwind-merge'

export interface CreditBlocksTableProps {
  /**
   * Array of credit blocks to display
   */
  creditBlocks: CreditBlock[]

  /**
   * Optional className for the container
   */
  className?: string

  /**
   * Whether the subscription is cancelled
   */
  isCancelled?: boolean
}

/**
 * Format date as a readable string
 */
const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A'

  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format date with time
 */
const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return 'N/A'

  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Determine if a credit block is expired
 */
const isExpired = (expiryDate: string | null, balance: number): boolean => {
  if (balance === 0) return true
  if (!expiryDate) return false

  const now = new Date()
  const expiry = new Date(expiryDate)
  return now > expiry
}

/**
 * Get status of credit block
 */
const getStatus = (expiryDate: string | null, balance: number): 'Active' | 'Inactive' => {
  return isExpired(expiryDate, balance) ? 'Inactive' : 'Active'
}

/**
 * Get type of credit block
 */
const getType = (expiryDate: string | null): 'monthly' | 'added' => {
  return expiryDate ? 'monthly' : 'added'
}

/**
 * CreditBlocksTable - Display credit history in a table format
 */
export const CreditBlocksTable: React.FC<CreditBlocksTableProps> = ({
  creditBlocks,
  className,
  isCancelled = false,
}) => {
  const [showExpired, setShowExpired] = useState(true)

  // Filter and sort blocks
  const processedBlocks = useMemo(() => {
    // Filter to only blocks that are effective (past effective date)
    const effectiveBlocks = creditBlocks.filter(
      (block) => block.effectiveDate && new Date(block.effectiveDate) <= new Date(),
    )

    // Filter based on showExpired toggle
    const visibleBlocks = showExpired
      ? effectiveBlocks
      : effectiveBlocks.filter((block) => !isExpired(block.expiryDate, block.balance))

    // Sort by effective date (most recent first)
    return [...visibleBlocks].sort((a, b) => {
      const dateA = a.effectiveDate ? new Date(a.effectiveDate).getTime() : 0
      const dateB = b.effectiveDate ? new Date(b.effectiveDate).getTime() : 0
      return dateB - dateA
    })
  }, [creditBlocks, showExpired])

  if (processedBlocks.length === 0) {
    return (
      <div className={twMerge('bg-secondary-old rounded-lg border p-6', className)}>
        <p className="text-foreground-secondary m-0 text-center text-sm">
          {showExpired ? 'No credit blocks available' : 'No active credit blocks available'}
        </p>
      </div>
    )
  }

  return (
    <div className={twMerge('bg-foreground/[2%] overflow-hidden rounded-lg', !isCancelled && 'border', className)}>
      {/* Table */}
      <div className="flex max-h-[600px] flex-col overflow-hidden">
        <div className="shrink-0 overflow-x-auto border-b">
          <table className="w-full min-w-0 table-fixed">
            <colgroup>
              <col className="w-24" />
              <col className="w-28" />
              <col className="w-24" />
              <col className="w-32" />
              <col className="hidden w-20 lg:table-column" />
              <col className="hidden w-32 lg:table-column" />
            </colgroup>
            <thead>
              <tr>
                <th className="text-foreground-secondary px-6 py-3 text-center text-xs font-medium tracking-wider">
                  Status
                </th>
                <th className="text-foreground-secondary px-6 py-3 text-center text-xs font-medium tracking-wider">
                  Amount
                </th>
                <th className="text-foreground-secondary px-6 py-3 text-center text-xs font-medium tracking-wider">
                  Type
                </th>
                <th className="text-foreground-secondary px-6 py-3 text-center text-xs font-medium tracking-wider">
                  Date
                </th>
                {/* <th className="px-6 py-3 text-center text-xs font-medium tracking-wider text-foreground-secondary">Balance</th> */}
                <th className="text-foreground-secondary hidden px-6 py-3 text-center text-xs font-medium tracking-wider lg:table-cell">
                  Used
                </th>
                <th className="text-foreground-secondary hidden px-6 py-3 text-center text-xs font-medium tracking-wider lg:table-cell">
                  Expiry Date
                </th>
              </tr>
            </thead>
          </table>
        </div>
        <FadingScrollable direction="vertical" className="flex-1" fadeSize="10%">
          <div className="overflow-x-auto">
            <table className="w-full min-w-0 table-fixed">
              <colgroup>
                <col className="w-24" />
                <col className="w-28" />
                <col className="w-24" />
                <col className="w-32" />
                <col className="hidden w-20 lg:table-column" />
                <col className="hidden w-32 lg:table-column" />
              </colgroup>
              <tbody className="divide-light-10 divide-y">
                {processedBlocks.map((block) => {
                  const status = getStatus(block.expiryDate, block.balance)
                  const type = getType(block.expiryDate)
                  const used = (block.initialBalance || 0) - block.balance
                  const usagePercent =
                    block.initialBalance && block.initialBalance > 0
                      ? Math.round((used / block.initialBalance) * 100)
                      : 0

                  return (
                    <tr key={block.id} className={cn('transition-opacity', status === 'Inactive' && 'opacity-50')}>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <span
                          className={cn(
                            'inline-flex items-center justify-center rounded-md px-2.5 py-0.5 text-xs font-medium',
                            status === 'Active'
                              ? 'bg-success/20 text-success-subtle'
                              : 'bg-red-80/10 text-foreground-secondary',
                          )}
                        >
                          {status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <span
                          className={cn(
                            'text-sm font-medium',
                            status === 'Active' ? 'text-foreground' : 'text-foreground-secondary',
                          )}
                        >
                          {block.initialBalance ? Math.round(block.initialBalance).toLocaleString() : 'N/A'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <div
                          className={cn(
                            'text-sm capitalize',
                            status === 'Active' ? 'text-foreground' : 'text-foreground-secondary',
                          )}
                        >
                          {type}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-center">
                        <span
                          className={cn(
                            'text-sm',
                            status === 'Active' ? 'text-foreground' : 'text-foreground-secondary',
                          )}
                        >
                          {formatDate(block.effectiveDate)}
                        </span>
                      </td>
                      {/* <td className="px-6 py-4 text-center whitespace-nowrap">
                        <span
                          className={cn('text-sm font-medium', status === 'Expired' ? 'text-light-40' : 'text-white')}
                        >
                          {Math.round(block.balance).toLocaleString()}
                        </span>
                      </td> */}
                      <td className="hidden whitespace-nowrap px-6 py-4 text-center lg:table-cell">
                        {block.initialBalance && block.initialBalance > 0 ? (
                          <span
                            className={cn(
                              'text-sm',
                              status === 'Active' ? 'text-foreground' : 'text-foreground-secondary',
                            )}
                          >
                            {usagePercent}%
                          </span>
                        ) : (
                          <span
                            className={cn(
                              'text-sm',
                              status === 'Active' ? 'text-foreground' : 'text-foreground-secondary',
                            )}
                          >
                            0%
                          </span>
                        )}
                      </td>
                      <td className="hidden whitespace-nowrap px-6 py-4 text-center lg:table-cell">
                        <span
                          className={cn(
                            'text-sm',
                            status === 'Active' ? 'text-foreground' : 'text-foreground-secondary',
                          )}
                        >
                          {block.expiryDate ? formatDate(block.expiryDate) : 'Never'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </FadingScrollable>
      </div>
    </div>
  )
}
